import { useState } from 'react';
import {
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListSprintsQuery,
  useCreateSprintMutation,
  useUpdateSprintMutation,
  useDeleteSprintMutation,
} from '../redux/firestoreApi';
import { setActiveSprint } from '../redux/sessionSlice';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { Sprint } from '../types/models';

interface FormData {
  sprintName: string;
  startDate: string;
  endDate: string;
  sprintGoal?: string;
  goalDescription?: string;
}

export default function SprintsPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const activeSprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();

  const { data: sprints = [], isLoading } = useListSprintsQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );
  const [create] = useCreateSprintMutation();
  const [update] = useUpdateSprintMutation();
  const [remove] = useDeleteSprintMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Sprint | null>(null);
  const [toDelete, setToDelete] = useState<Sprint | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<FormData>();

  if (!projectId) {
    return (
      <>
        <PageHeader title="Sprints" />
        <Alert severity="info">Select a project from the top bar to manage sprints.</Alert>
      </>
    );
  }

  const openCreate = () => {
    setEditing(null);
    reset({
      sprintName: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: '',
      sprintGoal: '',
      goalDescription: '',
    });
    setOpen(true);
  };

  const openEdit = (s: Sprint) => {
    setEditing(s);
    reset({
      sprintName: s.sprintName,
      startDate: s.startDate,
      endDate: s.endDate,
      sprintGoal: s.sprintGoal ?? '',
      goalDescription: s.goalDescription ?? '',
    });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (editing) {
        await update({ uid, projectId, id: editing.id, patch: data }).unwrap();
        dispatch(showToast({ message: 'Sprint updated', severity: 'success' }));
      } else {
        const created = await create({ uid, projectId, ...data }).unwrap();
        dispatch(setActiveSprint(created.id));
        dispatch(showToast({ message: 'Sprint created', severity: 'success' }));
      }
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Sprint deleted', severity: 'success' }));
      if (activeSprintId === toDelete.id) dispatch(setActiveSprint(null));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  return (
    <>
      <PageHeader
        title="Sprints"
        subtitle="Time-boxed iterations within the active project."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New sprint
          </Button>
        }
      />

      {!isLoading && sprints.length === 0 ? (
        <EmptyState
          title="No sprints yet"
          description="Create a sprint to start planning."
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create sprint
            </Button>
          }
        />
      ) : (
        <Grid container spacing={2}>
          {sprints.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: activeSprintId === s.id ? 'primary.main' : undefined,
                  borderWidth: activeSprintId === s.id ? 2 : 1,
                }}
              >
                <CardContent>
                  <Typography variant="h6">{s.sprintName}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip size="small" label={`Start ${s.startDate}`} />
                    <Chip size="small" label={`End ${s.endDate}`} />
                  </Stack>
                  {s.sprintGoal && (
                    <Typography sx={{ mt: 2 }} fontWeight={600}>
                      {s.sprintGoal}
                    </Typography>
                  )}
                  {s.goalDescription && (
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                      {s.goalDescription}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => dispatch(setActiveSprint(s.id))}
                    disabled={activeSprintId === s.id}
                  >
                    {activeSprintId === s.id ? 'Active' : 'Set active'}
                  </Button>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => openEdit(s)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setToDelete(s)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit sprint' : 'New sprint'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Sprint name"
                autoFocus
                error={!!errors.sprintName}
                helperText={errors.sprintName?.message}
                {...register('sprintName', { required: 'Required' })}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.startDate}
                  helperText={errors.startDate?.message}
                  {...register('startDate', { required: 'Required' })}
                />
                <TextField
                  label="End date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.endDate}
                  helperText={errors.endDate?.message}
                  {...register('endDate', { required: 'Required' })}
                />
              </Stack>
              <TextField label="Sprint goal" {...register('sprintGoal')} />
              <TextField
                label="Goal description"
                multiline
                rows={3}
                {...register('goalDescription')}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete sprint?"
        message={`"${toDelete?.sprintName}" and its task references will be removed.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
